/**
 * Ảnh bên trái mọi tên trên màn Phòng Kinh doanh (CEO 30/10/2026): người →
 * ảnh đại diện nhân sự; tiểu đội → logo tiểu đội đã đặt ở thẻ «06 Tiểu đội»
 * (QĐ 0485); đội / miền → logo đội / miền (QĐ 0492); chưa có logo → ô tròn chữ cái đầu.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { StaffAvatar } from "@/components/StaffAvatar";
import { StaffTitle } from "@/features/cosmetics/StaffTitle";
import { SquadLogo } from "@/features/sales-squad/SquadLogo";
import { useSquadBadgeMap } from "@/features/sales-squad/api";

/** Người chưa có hồ sơ GNPF mang mã tạm "hrm:<mã>" — không tra ảnh theo mã đó. */
function avatarId(staffId: string | null | undefined): string | undefined {
  return staffId && !staffId.startsWith("hrm:") ? staffId : undefined;
}

type PersonProps = {
  staffId?: string | null | undefined;
  name: string;
  size?: number;
  className?: string | undefined;
  children?: ReactNode;
  /**
   * Danh hiệu đang đeo sau tên (CEO 9a+b, 17/10/2026). Tắt ở ô chọn người và tên
   * nằm giữa câu — chèn vào là vỡ ô (cùng ngoại lệ `danh-hieu-canh-ten.test.ts`).
   */
  title?: boolean;
};

/** Ảnh nhân sự + tên (hoặc nội dung truyền vào) bên phải, kèm danh hiệu. */
export function PersonMark({ staffId, name, size = 24, className, children, title = true }: PersonProps) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      <StaffAvatar staffId={avatarId(staffId)} name={name} sizePx={size} />
      <span className="min-w-0">{children ?? name}</span>
      {title ? <StaffTitle staffId={avatarId(staffId)} name={name} /> : null}
    </span>
  );
}

type SquadProps = {
  /** Mã tiểu đội / đội / miền; chưa có logo hay vắng mã ⇒ chữ cái đầu. */
  squadId?: string | null | undefined;
  name: string;
  size?: number;
  className?: string | undefined;
  children?: ReactNode;
};

/** Logo tiểu đội / đơn vị + tên (hoặc nội dung truyền vào) bên phải. */
export function SquadMark({ squadId, name, size = 24, className, children }: SquadProps) {
  const badges = useSquadBadgeMap();
  const badge = squadId ? badges.get(squadId) : undefined;
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      <SquadLogo squad={badge ?? { name }} size={size} mode="ring" />
      <span className="min-w-0">{children ?? name}</span>
    </span>
  );
}
